import { isEqual } from 'lodash';
import { Attributes } from 'src/components/organisms';
import { useIsFeatureEnabled } from 'src/hooks';
import { FeatureFlag, SUBMISSION_MODE } from 'src/types';
import { DefaultOption } from '../DropdownSelect';
import {
  ParameterType,
  getAttributesOptions,
  mapSubmissionModeToReportSource,
} from './helpers';

export interface ParameterList {
  site?: string[];
  department?: string[];
  country?: string[];
  team?: string[];
  anonymous?: boolean[];
  relationshipToCompany?: string[];
  systemRoles?: string[];
  submissionMode?: number[];
  reportedCategory?: string[];
  'userMetaData.organization'?: string[];
  'userMetaData.division'?: string[];
  'Call.organisationHotlineNumber'?: string[];
  coiCategory?: string[];
  questionnaireId?: string[];
  ''?: string[];
}

export const useParameter = (
  parameter: ParameterType,
  parameterList: ParameterList,
  parameterOptions: DefaultOption[],
  attributes: Attributes,
) => {
  const noOptionsSelected = Object.keys(parameterList).length === 1;
  const emptyParameterExists = parameterList[''] !== undefined;
  const parameterWithNoValues = Object.keys(parameterList).some(
    (key) => parameterList[key as ParameterType]?.length === 0,
  );
  const isSubmissionMode =
    parameterList[parameter] && parameter === 'submissionMode';
  const isNotEmptyParameter =
    parameterList[parameter] && !isEqual(parameterList[parameter], ['']);
  const isRelationshipToOrgEnabled = useIsFeatureEnabled(
    FeatureFlag.RELATIONSHIP_TO_ORG,
  );

  const showParameterWarning =
    parameter === 'relationshipToCompany' && !isRelationshipToOrgEnabled;

  // Parameter based variables
  const parameterValue = parameterOptions.find((p) => p.value === parameter);
  const parameterOptionsList = isSubmissionMode
    ? (parameterList[parameter] as number[]).filter((option) => {
        // if Mobile App is selected, submission modes 1, 2, 3 are selected in the rule.
        // Remove 2, 3, so we only display Mobile App once
        // Same for Slack (7, 8) and Reporting Hub (11, 12)
        return ![
          SUBMISSION_MODE.APP_GO_ANONYMOUS,
          SUBMISSION_MODE.APP_GO_AHEAD,
          SUBMISSION_MODE.SLACK_GO_ANONYMOUS,
          SUBMISSION_MODE.REPORTER_PORTAL_GO_TOGETHER,
        ].includes(option);
      })
    : parameterList[parameter];

  const parameterOptionsWithNulls =
    parameterOptionsList?.map((option) => {
      const value = option;
      let label = option as string | number;
      if (isSubmissionMode) {
        label = mapSubmissionModeToReportSource(option as number);
      }
      if (option !== '') {
        const labelFromAttributes = attributes[
          parameter as keyof Attributes
        ]?.find((attribute) => attribute.value === option)?.label;
        if (isSubmissionMode && !labelFromAttributes) {
          return null;
        }
        label = labelFromAttributes ?? (option as string | number);
      }
      return {
        value,
        label,
      };
    }) ?? [];
  const parameterOptionsValue = parameterOptionsWithNulls.filter(
    (option) => option !== null,
  ) as DefaultOption[];

  const attributesOptions = getAttributesOptions(
    parameter,
    parameterOptionsValue,
    attributes,
  );

  // Feature based variables
  const oprEnabled = useIsFeatureEnabled(FeatureFlag.PUBLIC_REPORTING);
  const vaultTalkEnabled = useIsFeatureEnabled(FeatureFlag.VAULT_TALK);
  const warningText = `We only support "Relationship to Organization" for ${
    oprEnabled ? 'OPR' : ''
  }${oprEnabled && vaultTalkEnabled ? ' and ' : ''}${
    vaultTalkEnabled ? 'Vault Talk' : ''
  }.`;

  return {
    parameterValue,
    parameterOptionsValue,
    attributesOptions,
    noOptionsSelected,
    emptyParameterExists,
    parameterWithNoValues,
    isNotEmptyParameter,
    showParameterWarning,
    warningText,
  };
};
